import { useState } from "react";
import { useSpatial } from "../SpatialContext";

const TEAL = "#4DB8B5";

/** Public gate: request a magic link by email (new users + returning users after sign-out). */
export default function MagicLinkGate({ bootstrapping, authCallbackError }) {
  const { signInWithMagicLink } = useSpatial();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [sent, setSent] = useState(false);

  const hasStaleCode = !bootstrapping && /[?&]code=/.test(window.location.search) && !authCallbackError;

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSent(false);
    const trimmed = email.trim();
    if (!trimmed) {
      setFormError("Please enter your email address.");
      return;
    }
    setLoading(true);
    const { error } = await signInWithMagicLink(trimmed);
    setLoading(false);
    if (error) setFormError(error);
    else setSent(true);
  };

  const cardStyle = {
    width: "100%",
    maxWidth: 420,
    background: "rgba(255,255,255,0.97)",
    borderRadius: 16,
    padding: "32px 28px",
    boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
    textAlign: "center",
  };

  const inputStyle = {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.12)",
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    boxSizing: "border-box",
    marginBottom: 10,
  };

  const alertStyle = (variant) => ({
    marginTop: 16,
    padding: "10px 12px",
    borderRadius: 8,
    fontSize: 12,
    textAlign: "left",
    background: variant === "error" ? "#FEF2F2" : "#ECFDF5",
    border: variant === "error" ? "1px solid #FECACA" : "1px solid #A7F3D0",
    color: variant === "error" ? "#B91C1C" : "#047857",
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(160deg, #0F172A 0%, #1e293b 50%, #0F172A 100%)",
        padding: 24,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div style={cardStyle}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "rgba(77,184,181,0.15)",
            border: `1px solid ${TEAL}`,
            color: TEAL,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            fontWeight: 700,
            marginBottom: 12,
          }}
        >
          ✦
        </div>
        <h1 style={{ margin: "0 0 8px", fontSize: 20, color: "#1E293B" }}>NeuroQuest</h1>

        {bootstrapping ? (
          <>
            <p style={{ margin: "0 0 16px", fontSize: 14, color: "#64748B" }}>Completing sign-in…</p>
            <div
              style={{
                width: 28,
                height: 28,
                margin: "0 auto",
                borderRadius: "50%",
                border: `3px solid ${TEAL}`,
                borderTopColor: "transparent",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        ) : (
          <>
            <p style={{ margin: "0 0 6px", fontSize: 15, color: "#475569", lineHeight: 1.55 }}>
              Enter your email and we&apos;ll send you a <strong>magic link</strong> to sign in.
            </p>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#94A3B8", lineHeight: 1.5 }}>
              New here or signed out? Same flow - request a link, then open it from your inbox.
            </p>

            {sent ? (
              <div style={alertStyle("success")}>
                <strong>Check your email.</strong> We sent a sign-in link to {email.trim()}. Open it once to enter
                NeuroQuest.
              </div>
            ) : (
              <form onSubmit={handleRequestAccess} style={{ textAlign: "left" }}>
                <label htmlFor="access-email" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 6 }}>
                  Email address
                </label>
                <input
                  id="access-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  style={inputStyle}
                />
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: 10,
                    border: "none",
                    background: loading ? "rgba(77,184,181,0.4)" : TEAL,
                    color: "white",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: loading ? "default" : "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  {loading ? "Sending…" : "Request access"}
                </button>
                <p style={{ margin: "10px 0 0", fontSize: 11, color: "#94A3B8", lineHeight: 1.5, textAlign: "center" }}>
                  If you don't see the sign-in email, check spam or junk. Mark it as not spam and future ones will land in your inbox.
                </p>
              </form>
            )}

            {sent && (
              <button
                type="button"
                onClick={() => { setSent(false); setFormError(null); }}
                style={{
                  marginTop: 12,
                  background: "none",
                  border: "none",
                  color: TEAL,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Use a different email
              </button>
            )}
          </>
        )}

        {formError && <div style={alertStyle("error")}>{formError}</div>}

        {(authCallbackError || hasStaleCode) && !bootstrapping && (
          <div style={alertStyle("error")}>
            {authCallbackError ||
              "This sign-in link could not be used (expired or already opened). Request a new link above and open it once."}
          </div>
        )}
      </div>
    </div>
  );
}
